import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export const Hero = () => {
  const { hero } = useSiteSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const link = hero?.button_link || "/collection/all";
  const isExternal = /^https?:\/\//i.test(link);
  const btnStyle = { backgroundColor: hero?.button_color || "#1A1A1A", color: hero?.button_text_color || "#ffffff" };
  const btnClass =
    "inline-flex items-center justify-center rounded-none px-9 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500 hover:opacity-90";

  return (
    <section ref={ref} className="relative min-h-[88vh] overflow-hidden bg-beige" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={hero?.image || "/hero-chocolates.jpg"} alt={hero?.heading || "LIRA"} className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-transparent" />
      </motion.div>

      <div className="relative container-lira min-h-[88vh] flex items-center">
        <div className="max-w-2xl">
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-bold leading-[1.1] tracking-wide text-2xl sm:text-4xl md:text-5xl whitespace-nowrap"
            style={{ fontFamily: hero?.heading_font || '"Cinzel", serif', color: hero?.heading_color || "#ffffff" }}
            data-testid="hero-heading"
          >
            {hero?.heading || "LIRA CHOCOLATES"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-4 text-base sm:text-lg md:text-xl"
            style={{ fontFamily: hero?.subheading_font || '"Times New Roman", Times, serif', color: hero?.subheading_color || "#ffffff" }}
            data-testid="hero-subheading"
          >
            {hero?.subheading || "Everyday fall essentials."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10"
          >
            {isExternal ? (
              <a href={link} className={btnClass} style={btnStyle} data-testid="hero-shop-button">
                {hero?.button_text || "Shop Collection"} <ArrowRight strokeWidth={1.2} className="h-4 w-4 ml-2" />
              </a>
            ) : (
              <Link to={link} className={btnClass} style={btnStyle} data-testid="hero-shop-button">
                {hero?.button_text || "Shop Collection"} <ArrowRight strokeWidth={1.2} className="h-4 w-4 ml-2" />
              </Link>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
